/* import __COLOCATED_TEMPLATE__ from './location-dropdown.hbs.js'; */
/**
### Overview

This component is largely based on sq-subunits-filter, but with a modified API
and better naming. It's used by the `location-selector` component to handle all
selection and UI concerns.

If possible, you should consider using the parent `location-selector` component,
which handles interaction with the `location-selector` service for you automatically.
You may consider using this component directly if you need multiple location selectors
backed by different data on the same page, or in nested routes, but please consider
whether that sort of visual complexity is necessary and helpful.

This is a strictly a data-down/actions-up component. Binding to the
`value` property will not work. Instead, listen for changes to the selected
subunits using the `change` action. Then store that value on the property
passed to `value` and the filter will stay in sync.

```example
@title Multiple Selection

@controller-properties
{
  units: [{
    nickname: 'Location D',
    token: 'location-d'
  }, {
    nickname: 'Location A',
    token: 'location-a'
  }, {
    nickname: 'Location C',
    token: 'location-c'
  }, {
    nickname: 'Location B',
    token: 'location-b'
  }],

  selectedUnits: Ember.computed(function() {
    return [this.get('units.firstObject')];
  }),

  selectedUnitNicknames: Ember.computed('selectedUnits', function() {
    return this.get('selectedUnits').mapBy('nickname').join(', ');
  }),

  actions: {
    locationDropdownChange(value) {
      this.set('selectedUnits', value);
    },

    trackOpen() {
      console.log('location-dropdown component was opened!');
    }
  }
}

@hbs
{{location-selector/location-dropdown multiple=true
                                      units=units
                                      selectedUnits=selectedUnits
                                      onChange=(action "locationDropdownChange")
                                      onOpen=(action "trackOpen")}}
<p>Value: {{selectedUnitNicknames}}</p>
```

```example
@title Single Selection

@controller-properties
{
  units: [{
    nickname: 'Location D',
    token: 'location-d'
  }, {
    nickname: 'Location A',
    token: 'location-a'
  }, {
    nickname: 'Location C',
    token: 'location-c'
  }, {
    nickname: 'Location B',
    token: 'location-b'
  }],

  selectedUnit: Ember.computed.reads('units.firstObject'),

  actions: {
    locationDropdownChange(value) {
      this.set('selectedUnit', Ember.get(value, 'firstObject'));
    }
  }
}

@hbs
{{location-selector/location-dropdown units=units
                                      selectedUnits=selectedUnit
                                      onChange=(action "locationDropdownChange")}}
<p>Value: {{selectedUnit.nickname}}</p>
```

### Parameters
@param {Array} [units=[]] Array of unit objects that can be selected. Should already be sorted in display order.
@param {Array} [selectedUnits=[]] Array of unit objects that are currently selected.
@param {Action} [onChange] Sets the action sent when the value is changed.
@param {Action} [onOpen] Sets the action sent when the dropdown is opened.
@param {Boolean} [hideIfOneUnit=true] Set to `false` to show the filter when there is only one unit.
@param {Boolean} [isDisabled=false] Set to `true` to disable location dropdown entirely and not allow for any selections to be made.
@param {Boolean} [multiple=false] Set to `true` to allow merchants to select multiple units and adds the "Select All | Clear Selection" UI at the bottom.
@param {Boolean} [useAsFilter=false] Set to `true` to treat the selector as a filter - when no units are selected "All locations" is displayed instead of "No locations"
@param {Boolean} [alignRight=false] Set to `true` to align dropdown on right.
@param {String} [alternateLabel=null] Set to override the default label with a custom label
@param {Boolean} [staticHeight=true] - When multiple=false, determines the rendering strategy for vertical collection. Indicates if the occluded items' heights will change or not. If true, the vertical-collection will assume that items' heights are always equal to estimateHeight;
@param {Number} [estimateHeight=50] - When multiple=false, estimated height of an item to be rendered for vertical collection. Use best guess as this will be used to determine how many items are displayed virtually, before and after the vertical-collection viewport.
@param {Number} [bufferSize=20] - When multiple=false, used by vertical collection to determine the amount of extra items to keep visible on either side of the viewport -- must be greater than 0.
 */

import Component from '@ember/component';
import { computed, set } from '@ember/object';
import { and } from '@ember/object/computed';
import { A, makeArray } from '@ember/array';
import { typeOf } from '@ember/utils';
import { assert } from '@ember/debug';
import { subunitsLabel } from '@dashboard/junk-drawer/utils/subunits';
import marketFilterDisplayValue from '@dashboard/shared-ui/utils/market-filter-display-value';
import { inject as service } from '@ember/service';
function copyAsArray(value) {
  // unwrap array proxies
  let arr;
  if (value && typeOf(value.toArray) === 'function') {
    arr = value.toArray();
  } else {
    arr = value;
  }
  return A([...makeArray(arr)]);
}

// eslint-disable-next-line ember/no-classic-classes
export default Component.extend({
  classNames: ['sq-subunits-filter'],
  showComponent: computed('hideIfOneUnit', 'units.length', function () {
    // Show the component regardless of the number of units the merchant has if hideIfOneUnit flag is disabled.
    if (!this.hideIfOneUnit) {
      return true;
    }

    // Show the component only if the merchant has more than one unit and hideIfOneUnit flag is enabled.
    return this.units.length > 1;
  }),
  // passed in
  hideIfOneUnit: true,
  hideMarketSelectableCount: false,
  multiple: false,
  useAsFilter: false,
  units: null,
  selectedUnits: null,
  alignRight: false,
  isDisabled: false,
  isMarketUi: false,
  filterButtonSize: 'medium',
  alternateLabel: null,
  // default values for vertical-collection, used when multiple=false
  estimateHeight: 50,
  staticHeight: true,
  bufferSize: 20,
  // Search box filter
  searchFilter: '',
  internalSubunitOptions: null,
  allValueTrigger: 0,
  // change to force a recalculation of the all row value

  i18n: service(),
  init(...args) {
    this._super(...args);
    if (this.staticHeight || this.estimateHeight || this.bufferSize) {
      // TODO: CRM-4247: Reverse the order of parameters to this assert so that it actually works (and fix resulting test failures).
      // eslint-disable-next-line ember/no-invalid-debug-function-arguments
      assert(this.multiple, 'Only pass staticHeight/estimateHeight/bufferSize when using `multiple`.');
    }
  },
  didReceiveAttrs() {
    this._super();
    const selectedUnits = this.selectedUnits;
    const internalSubunitOptions = this.units.map(subunit => ({
      unit: subunit,
      checked: selectedUnits.includes(subunit),
      disabled: Boolean(subunit.disabled)
    }));
    this.set('internalSubunitOptions', internalSubunitOptions);
  },
  label: computed('alternateLabel', 'isMarketUiMultipleSelector', 'i18n', 'multiple', 'selectedUnits.[]', 'units.length', 'useAsFilter', function () {
    if (this.alternateLabel) {
      return this.alternateLabel;
    }
    if (this.isMarketUiMultipleSelector) {
      return this.i18n.t('subunit.subunits');
    }
    const selectedUnits = copyAsArray(this.selectedUnits);

    // `subunitsLabel` returns the properly formatted string based on the number of
    // locations. (e.g. "All locations" or "5 Locations" or "Unit A and Unit B").
    if (this.multiple) {
      return subunitsLabel(selectedUnits.mapBy('nickname'), this.units.length, {
        useAsFilter: this.useAsFilter
      });
    }
    return selectedUnits.firstObject?.nickname;
  }),
  displayValue: computed('selectedUnits.[]', 'units.length', function () {
    const selectedUnits = copyAsArray(this.selectedUnits).map(unit => unit.nickname);
    return marketFilterDisplayValue(selectedUnits, this.units.length);
  }),
  selectedUnitTokens: computed('selectedUnits.[]', 'allValueTrigger', function () {
    const selectedUnitTokensWithTrigger = this.selectedUnits.map(({
      token
    }) => token);
    selectedUnitTokensWithTrigger.push(this.allValueTrigger);
    return selectedUnitTokensWithTrigger.join(',');
  }),
  singleValue: computed('selectedUnits.[]', 'units', function () {
    const selectedUnits = copyAsArray(this.selectedUnits);
    return selectedUnits[0];
  }),
  isMarketUiMultipleSelector: and('multiple', 'isMarketUi'),
  isMarketUiSingleSelector: computed('multiple', 'isMarketUi', function () {
    return this.isMarketUi && !this.multiple;
  }),
  actions: {
    sendSelectedUnits() {
      const selectedUnits = this.internalSubunitOptions.filter(({
        checked
      }) => checked).map(({
        unit
      }) => unit);
      this.onChange?.(copyAsArray(selectedUnits));
    },
    marketSendSelectedUnits(event) {
      const selectedUnitTokens = event.detail.currentSelectionValues;
      const selectedUnits = this.units.filter(({
        token
      }) => selectedUnitTokens.includes(token));
      this.onChange?.(copyAsArray(selectedUnits));
    },
    // multiple=false actions

    singleValueChange(unit) {
      this.onChange?.([unit]);
    },
    marketLocationDropdownOpened() {
      this.onOpen?.();
      if (this.multiple) {
        set(this, 'allValueTrigger', this.allValueTrigger + 1);
      }
    }
  }
});